import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronDown, CreditCard, Globe, Wallet2, Snowflake, Ban, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApp } from "@/App";
import { cn } from "@/lib/utils";
import CardFace from "@/components/CardFace";
import { CardsSkeleton, CardsUnavailable } from "@/components/CardsFallback";
import EmailRequiredDialog from "@/components/EmailRequiredDialog";
import CardsScene from "@/components/cards/CardsScene";
import {
  BLOCKED_COUNTRIES,
  BLOCKED_MCC_GROUPS,
  BLOCKED_MCC_TOTAL,
} from "@/components/cards/issuerLimits";

// Экран не спрашивает сумму. Владельцы решили, что карта выпускается по
// фиксированной цене, а сумму первичного зачисления определяет сервер
// (CARDS_ISSUE_INITIAL_MINOR) — совсем без баланса провайдер карту не выпускает,
// у initialAmountMinor в его схеме стоит minimum: 1.
//
// Поэтому здесь нет ни поля ввода, ни пресетов, ни проверок на минимум и
// максимум: человек видит одно число и одну кнопку.
const IssueCardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { api, haptic, isTelegramWebApp, setBackHandler, clearBackHandler } = useApp();
  const showInlineBack = !isTelegramWebApp;

  const [config, setConfig] = useState(null);
  // Конфиг здесь обязателен: без цены показывать нечего. Поэтому его отказ —
  // состояние экрана с кнопкой «Повторить», а не тост, после которого остаётся
  // пустой экран с неактивной кнопкой выпуска.
  const [configError, setConfigError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Готовность к выпуску живёт отдельно от конфига и не зависит от провайдера:
  // сказать «привяжите почту» экран обязан даже тогда, когда 2328 молчит.
  const [eligibility, setEligibility] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  // Свёрнут только блок ограничений эмитента: тарифы человек обязан видеть до
  // оплаты, а не разворачивать их сам.
  const [limitsOpen, setLimitsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const emailLinked = eligibility ? eligibility.emailLinked : true;

  const handleBack = useCallback(() => {
    haptic?.("light");
    navigate(-1);
  }, [haptic, navigate]);

  useEffect(() => {
    if (!isTelegramWebApp) return;
    setBackHandler(handleBack);
    return () => clearBackHandler();
  }, [isTelegramWebApp, setBackHandler, clearBackHandler, handleBack]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setConfigError(null);
      try {
        const res = await api.get("/cards/config");
        if (!cancelled) setConfig(res?.data || null);
      } catch (err) {
        if (!cancelled) {
          setConfigError(err?.response?.data?.detail || { message: t("cards.load_error") });
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [api, t, reloadKey]);

  // Проверка почты идёт на входе, а не по нажатию «Выпустить»: до этого человек
  // проходил весь экран, чтобы в конце получить отказ email.required.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/cards/eligibility")
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || null;
        setEligibility(data);
        if (data && !data.emailLinked) setEmailDialogOpen(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [api, reloadKey]);

  // Возвращаемся ровно сюда: привязка почты — это шаг внутри выпуска, а не
  // отдельный поход в профиль, из которого человек ищет дорогу назад.
  const goLinkEmail = useCallback(() => {
    haptic?.("light");
    navigate(
      `/profile/account?link=email&return=${encodeURIComponent("/cards/issue")}`
    );
  }, [haptic, navigate]);

  const money = (minor) =>
    minor === null || minor === undefined || minor < 0
      ? "—"
      : `$${(minor / 100).toFixed(2)}`;

  // Все три числа приходят с сервера уже посчитанными. Складывать их здесь
  // нельзя: цена на экране обязана совпадать с тем, что спишут на самом деле,
  // а считает это бэкенд по котировке провайдера.
  const feeMinor = config?.issueFeeMinor;
  const initialMinor = config?.initialAmountMinor;
  const totalMinor = config?.issueTotalMinor;
  const priceKnown = totalMinor !== undefined && totalMinor !== null && totalMinor >= 0;

  const balanceMinor = eligibility?.balanceMinor;
  const notEnough =
    priceKnown && balanceMinor !== undefined && balanceMinor !== null && balanceMinor < totalMinor;

  // Комиссии, которые держатель платит со своей карты. Приходят от провайдера,
  // а не задаются здесь: тариф меняется без нашего участия.
  const cardFees = config?.cardFees || {};
  const tariffRows = [
    ["fund", t("cards.fee_fund"), null, (config?.fundFeeBps || 0) + (config?.fundMarkupPct || 0) * 100],
    // Наша доля показывается рядом с провайдерской, а не вместо неё: человек
    // должен видеть ту же цену, которую с него возьмут.
    ["transaction", t("cards.fee_transaction"),
     (cardFees.transaction?.flatMinor || 0) + (config?.transactionMarkupMinor || 0),
     (cardFees.transaction?.bps || 0) + (config?.transactionMarkupPct || 0) * 100],
    ["fx", t("cards.fee_fx"), cardFees.fx?.flatMinor, cardFees.fx?.bps],
    ["decline", t("cards.fee_decline"), cardFees.decline?.flatMinor, cardFees.decline?.bps],
    ["maintenance", t("cards.fee_maintenance"), cardFees.maintenance?.flatMinor, cardFees.maintenance?.bps],
    ["chargeback", t("cards.fee_chargeback"), cardFees.chargeback?.flatMinor, cardFees.chargeback?.bps],
    // Вывода здесь не было вовсе: человек узнавал эту комиссию только дойдя до
    // экрана вывода, то есть уже после того, как завёл деньги на карту.
    ["unload", t("cards.fee_unload"), config?.unloadFlatMinor,
     (config?.unloadFeeBps || 0) + (config?.unloadMarkupPct || 0) * 100],
  ].filter(([, , flat, bps]) => Boolean(flat) || Boolean(bps));

  const feeText = (flat, bps) => {
    const parts = [];
    if (bps) parts.push(`${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 1)}%`);
    if (flat) parts.push(money(flat));
    return parts.join(" + ") || "—";
  };

  const handleSubmit = async () => {
    if (submitting) return;
    haptic?.("medium");
    setSubmitting(true);
    try {
      // Тело пустое: сумму определяет сервер. Присылать её с экрана значило бы
      // держать цену в двух местах, которые однажды разойдутся.
      const res = await api.post("/cards/issue", {});
      const orderId = res?.data?.orderId;
      if (!orderId) throw new Error("no order");

      // Провайдер ответил «принято» — дальше держать человека на экране
      // загрузки незачем. Выпуск идёт около полутора минут, и всё это время он
      // сидел бы перед спиннером, а закрыв приложение — терял бы и его. Вместо
      // этого уводим в список, где выпускаемая карта уже стоит плиткой:
      // видно, что дело сделано, и можно уйти. Результат придёт сообщением, а
      // ордер доведёт фоновый поллер.
      haptic?.("success");
      toast.success(t("cards.issue_started"));
      navigate("/cards?list=1", { replace: true });
      return;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "email.required") {
        // Страховка: почту могли отвязать в другой вкладке уже после того, как
        // экран получил eligibility. Объясняем тем же окном, а не выбрасываем
        // человека в профиль тостом.
        setEligibility((prev) => ({ ...(prev || {}), emailLinked: false, email: null }));
        setEmailDialogOpen(true);
        return;
      }
      if (!err?.response) {
        // Ответа от сервера НЕ БЫЛО: обрыв связи, таймаут, свёрнутое
        // приложение. Про такой исход мы не знаем ничего — карта могла и
        // выпуститься. Говорить «деньги возвращены» здесь нельзя: однажды это
        // уже было сказано поверх успешно выпущенной карты. Отправляем
        // смотреть список, где правда.
        toast.message(t("cards.issue_unknown"));
        navigate("/cards?list=1", { replace: true });
        return;
      }
      toast.error(detail?.message || t("cards.issue_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const blocked = !config || submitting || !emailLinked || notEnough || !priceKnown;

  const perks = [
    [Globe, t("cards.perk_world")],
    [Wallet2, t("cards.feature_topup")],
    [CreditCard, t("cards.perk_wallet")],
    [Snowflake, t("cards.feature_freeze")],
  ];

  return (
    <CardsScene testId="issue-card-page">
      <div className="lp-bar lp-topbar sticky top-0 z-30 grid grid-cols-[2rem_1fr_2rem] items-center gap-1.5 px-4 py-3">
        {showInlineBack ? (
          <button
            type="button"
            onClick={handleBack}
            className="lp-press inline-flex h-8 w-8 items-center justify-center text-foreground"
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        ) : (
          <span aria-hidden="true" className="h-8 w-8" />
        )}
        <h1 className="text-center text-[17px] font-semibold tracking-tight text-foreground">
          {t("cards.issue_title")}
        </h1>
        <span aria-hidden="true" className="h-8 w-8" />
        <span aria-hidden="true" className="lp-hair absolute inset-x-0 bottom-0 h-px" />
      </div>

      <div className="relative z-[1] flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-4 py-5 pb-[calc(6rem+var(--lp-bottom))] space-y-4">
        {!config ? (
          configError ? (
            <CardsUnavailable
              title={t("cards.unavailable_title")}
              hint={configError.message || t("cards.unavailable_hint")}
              retryLabel={t("cards.retry")}
              onRetry={() => {
                haptic?.("light");
                setReloadKey((n) => n + 1);
              }}
            />
          ) : (
            <CardsSkeleton />
          )
        ) : (
          <>
            <CardFace scheme={config.scheme || "visa"} className="mx-auto w-full max-w-sm" />

            <div className="px-2 text-center">
              <h2 className="text-base font-bold tracking-tight text-foreground">
                {t("cards.empty_title")}
              </h2>
              <p className="mt-1 text-[12px] text-muted-foreground">{t("cards.issue_tagline")}</p>
            </div>

            <div className="lp-glass overflow-hidden">
              {perks.map(([Icon, text], index) => (
                <div
                  key={text}
                  className={cn(
                    "flex items-center gap-3 px-3.5 py-2.5",
                    index > 0 && "border-t border-[var(--lp-hair)]"
                  )}
                >
                  <span className="lp-tile flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px]">
                    <Icon className="h-4 w-4 text-link" />
                  </span>
                  <span className="text-[13px] text-foreground">{text}</span>
                </div>
              ))}
            </div>

            {/* Что происходит с картой в момент выпуска. Раньше на этом месте
                человек выбирал сумму; теперь ему просто говорят результат. */}
            <div className="lp-glass flex items-center gap-3 p-3.5">
              <span className="lp-tile flex h-11 w-11 shrink-0 items-center justify-center rounded-full">
                <CreditCard className="h-5 w-5 text-link" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-foreground">
                  {t("cards.issue_initial_title", { amount: money(initialMinor) })}
                </span>
                <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                  {t("cards.issue_initial_hint")}
                </span>
              </span>
            </div>

            <div className="lp-glass space-y-2 p-3.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("cards.issue_fee")}</span>
                <span className="tabular-nums text-foreground">{money(feeMinor)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("cards.issue_initial")}</span>
                <span className="tabular-nums text-foreground">{money(initialMinor)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-[var(--lp-hair)] pt-2 text-base font-semibold">
                <span className="text-foreground">{t("cards.issue_total_due")}</span>
                <span className="tabular-nums text-link">{money(totalMinor)}</span>
              </div>
              <div className="flex items-center justify-between pt-1 text-xs">
                <span className="text-muted-foreground">{t("cards.issue_balance")}</span>
                <span className={cn("tabular-nums", notEnough ? "text-destructive-fg" : "text-muted-foreground")}>
                  {balanceMinor === undefined || balanceMinor === null
                    ? "—"
                    : `${(balanceMinor / 100).toFixed(2)} ${eligibility?.balanceCurrency || "USDT"}`}
                </span>
              </div>
            </div>

            {/* Комиссии, о которых человек иначе узнаёт из выписки: $0.30 за
                операцию и процент за конвертацию на мелком платеже дают
                заметную долю. Раньше блок был свёрнут — теперь открыт: это
                часть цены, и прятать её за нажатием неправильно. */}
            {tariffRows.length > 0 && (
              <div className="lp-glass overflow-hidden">
                <div className="border-b border-[var(--lp-hair)] px-3.5 py-2.5">
                  <span className="block text-sm font-semibold text-foreground">
                    {t("cards.tariffs_title")}
                  </span>
                  <span className="mt-0.5 block text-[11px] text-muted-foreground">
                    {t("cards.tariffs_hint")}
                  </span>
                </div>
                {tariffRows.map(([key, label, flat, bps], index) => (
                  <div
                    key={key}
                    className={cn(
                      "flex items-center justify-between px-3.5 py-2.5 text-xs",
                      index > 0 && "border-t border-[var(--lp-hair)]"
                    )}
                  >
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-semibold tabular-nums text-foreground">{feeText(flat, bps)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Правила эмитента уровня программы. Свёрнуты: они длинные и нужны
                не всем, но узнать про них из отказа на кассе — худший вариант,
                поэтому они здесь, а не в справке. */}
            <div className="lp-glass overflow-hidden">
              <button
                type="button"
                onClick={() => setLimitsOpen((v) => !v)}
                className="lp-press flex w-full items-center justify-between gap-3 px-3.5 py-3 text-left"
                aria-expanded={limitsOpen}
                data-testid="issue-limits-toggle"
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-foreground">
                    {t("cards.limits_title")}
                  </span>
                  <span className="mt-0.5 block text-[11px] text-muted-foreground">
                    {t("cards.limits_subtitle", { total: BLOCKED_MCC_TOTAL })}
                  </span>
                </span>
                <ChevronDown
                  className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", limitsOpen && "rotate-180")}
                />
              </button>

              {limitsOpen && (
                <div className="border-t border-[var(--lp-hair)] px-3.5 py-3 space-y-3.5">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                      {t("cards.limits_countries")}
                    </p>
                    <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                      {t("cards.limits_countries_hint")}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {BLOCKED_COUNTRIES.map((code) => (
                        <span key={code} className="lp-tile rounded-lg px-2 py-1 text-[11px] text-foreground">
                          <span className="mr-1 font-bold tabular-nums text-destructive-fg">{code}</span>
                          {t(`cards.country_${code}`)}
                        </span>
                      ))}
                    </div>
                  </div>

                  {BLOCKED_MCC_GROUPS.map(({ key, codes }) => (
                    <div key={key} className="border-t border-[var(--lp-hair)] pt-3">
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                          {t(`cards.limits_${key}`)}
                        </p>
                        <span className="text-[11px] tabular-nums text-muted-foreground">{codes.length}</span>
                      </div>
                      <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                        {t(`cards.limits_${key}_hint`)}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {codes.map((code) => (
                          <span
                            key={code}
                            className="lp-tile rounded-lg px-2 py-1 text-[11px] font-semibold tabular-nums text-muted-foreground"
                          >
                            {code}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="flex items-start gap-2 border-t border-[var(--lp-hair)] pt-3">
                    <Info className="mt-px h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                      {t("cards.limits_note")}
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Ban className="mt-px h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                      {t("cards.limits_no_atm")}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {config && (
        <div className="lp-bar relative z-[3] px-4 py-3 pb-[calc(0.75rem+var(--lp-bottom))]">
          <span aria-hidden="true" className="lp-hair absolute inset-x-0 top-0 h-px" />
          {notEnough ? (
            <button
              type="button"
              onClick={() => {
                haptic?.("light");
                navigate("/topup");
              }}
              className="lp-press w-full rounded-[14px] bg-primary px-4 py-3.5 text-sm font-semibold text-primary-foreground"
              data-testid="issue-topup"
            >
              {t("cards.issue_topup_cta")}
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={blocked}
              className={cn(
                "lp-press w-full rounded-[14px] px-4 py-3.5 text-sm font-semibold",
                blocked
                  ? "lp-tile text-muted-foreground"
                  : "bg-primary text-primary-foreground shadow-[0_8px_20px_-10px_hsl(var(--primary))]"
              )}
              data-testid="issue-submit"
            >
              {submitting
                ? t("cards.issuing_title")
                : priceKnown
                ? t("cards.issue_submit_amount", { amount: money(totalMinor) })
                : t("cards.issue_submit")}
            </button>
          )}
          {notEnough ? (
            <p className="mt-2 text-center text-xs text-destructive-fg">
              {t("cards.issue_not_enough")}
            </p>
          ) : !emailLinked ? (
            <button
              type="button"
              onClick={() => setEmailDialogOpen(true)}
              className="mt-2 w-full text-center text-xs text-link"
              data-testid="issue-email-hint"
            >
              {t("cards.email_required_badge")}
            </button>
          ) : priceKnown ? (
            <p className="mt-2 text-center text-xs text-muted-foreground">
              {t("cards.issue_initial_title", { amount: money(initialMinor) })}
            </p>
          ) : null}
        </div>
      )}

      {/* Сюда без почты попадают только по прямой ссылке или после отвязки в
          другой вкладке: обычный путь перехватывает экран списка карт. «Позже»
          возвращает к списку — оставлять человека на экране, где кнопка
          заблокирована, значит запереть его в тупике. */}
      <EmailRequiredDialog
        open={emailDialogOpen}
        onGoToProfile={goLinkEmail}
        onLater={() => {
          setEmailDialogOpen(false);
          navigate("/cards?list=1", { replace: true });
        }}
      />
    </CardsScene>
  );
};

export default IssueCardPage;
